import "./style.css";
import { createViewport, type ViewMode } from "@/render/viewport";
import { StructureView } from "@/render/structureView";
import { AppurtenanceView } from "@/render/appurtenanceView";
import { Atmosphere } from "@/render/atmosphere";
import { WindView } from "@/render/windView";
import { Solver } from "@/physics/solver";
import { velocityPressure } from "@/physics/wind";
import { listDesigns, getDesign, type BuiltTower } from "@/game/towers";
import {
  compileTower,
  towerMetrics,
  resolveFoundationFor,
  type TowerSpec,
  type TowerDesign,
  type FreeGraph,
} from "@/game/towerSpec";
import { TowerDesigner } from "@/game/designer";
import { FreeBuilder } from "@/game/freeBuild";
import {
  saveLocal,
  listLocal,
  deleteLocal,
  exportFile,
  importFile,
  encodeShareUrl,
  decodeShareUrl,
} from "@/game/persistence";
import {
  ChallengePanel,
  evaluateChallenge,
  type Challenge,
} from "@/game/challenges";
import { WIND_PRESETS } from "@/game/windPresets";
import { groundHeight } from "@/render/terrain";
import { Schematic2D } from "@/render/schematic2d";
import { FailureReplay } from "@/game/failureReplay";
import { compressionCapacity, tensionCapacity, connectionCapacity, type Structure } from "@/physics/model";
import { DynamicsAnalyzer } from "@/physics/advancedPhysics";
import { STEEL_DAMPING, gustSpeed, DURST_IU } from "@/physics/gustModel";
import { EventLog } from "@/game/eventLog";
import { PartInspector } from "@/game/partInspector";
import { estimateCost, towerEconomics, leaseBreakdown } from "@/game/costEstimator";
import { loadEPA } from "@/game/appurtenances";
import {
  Raycaster,
  Vector2,
  Vector3,
  Quaternion,
  Mesh,
  CylinderGeometry,
  MeshBasicMaterial,
} from "three";

const ICE_DENSITY_KG_M3 = 900;

const canvas = document.getElementById("scene") as HTMLCanvasElement;
const viewport = createViewport(canvas);
const atmosphere = new Atmosphere(viewport.scene); // drifting clouds + circling birds
viewport.onFrame((dt) => atmosphere.update(dt));
// Wind-flow visualization — streaks showing the real airflow (direction, height
// shear, gusts, wake vortex shedding). Runs on its own frame hook so it keeps
// animating even while the sim is paused (e.g. during a failure replay).
const windView = new WindView(viewport.scene);
viewport.onFrame((dt) =>
  windView.update(dt, {
    windSpeed: currentWind,
    windDir: solver?.params.windDir ?? 0,
    gustiness: solver?.params.gustiness ?? 0,
    windSeed: solver?.params.windSeed ?? 1,
  }),
);
let schematic: Schematic2D;

// Live dynamics explainer: a scrollable, cited timeline of the aeroelastic
// phenomena (resonance, VIV lock-in, gust amplification, …) the analyzer detects
// each frame from the tower's REAL geometry/mass/wind.
const eventLogEl = document.getElementById("event-log")!;
const eventLog = new EventLog(eventLogEl);
eventLogEl.style.display = "none"; // only shown while replaying a failure

// Failure replay: records members as they fail and lets the player step through
// them, zooming the camera to each break. Pauses the live sim while replaying.
let simPaused = false;
let simTime = 0;
let maxSwayM = 0; // peak horizontal top-sway (m) since the tower was (re)built
const replay = new FailureReplay(document.getElementById("replay")!, {
  setPaused: (p) => {
    simPaused = p;
    eventLogEl.style.display = p ? "" : "none"; // event log visible only in replay
  },
  focusOn: (t, radius) => {
    viewport.controls.target.set(t.x, t.y, t.z);
    viewport.controls.object.position.set(t.x + radius, t.y + radius * 0.6, t.z + radius);
    viewport.controls.update();
  },
  captureSnapshot: snapshotNodes,
  applySnapshot: applyNodes,
});

// Snapshot / restore node positions so the replay can rewind the tower to how it
// looked at each failure moment (part shown in situ, not where debris landed).
function snapshotNodes(): Float32Array {
  const nodes = built.structure.nodes;
  const arr = new Float32Array(nodes.length * 3);
  for (let i = 0; i < nodes.length; i++) {
    const nd = nodes[i]!;
    arr[i * 3] = nd.x;
    arr[i * 3 + 1] = nd.y;
    arr[i * 3 + 2] = nd.z;
  }
  return arr;
}
function applyNodes(pos: Float32Array): void {
  const nodes = built.structure.nodes;
  const n = Math.min(nodes.length, Math.floor(pos.length / 3));
  for (let i = 0; i < n; i++) {
    const nd = nodes[i]!;
    nd.x = pos[i * 3]!;
    nd.y = pos[i * 3 + 1]!;
    nd.z = pos[i * 3 + 2]!;
    nd.px = nd.x; // zero velocity so resuming doesn't jolt
    nd.py = nd.y;
    nd.pz = nd.z;
  }
  view?.setWind(currentWind, currentWindDir); // flag streams downwind, still when calm
  view?.update();
  appView?.update();
  updateHighlight();
  schematic.update();
}

/**
 * Build the monospace physics line for a member that just failed, straight from
 * its real values at the instant of failure (forces in N, section/material data).
 *
 * Buckle (compression): reports the actual compression force vs the governing
 * capacity — Euler Pcr = π²·E·I/(K·L0)² or yield A·Fy, whichever is min — plus
 * the slenderness λ = K·L0/√(I/A).
 * Snap (tension): reports the actual tension vs the yield capacity A·Fy, with the
 * area A (cm²) and yield Fy (MPa).
 */
function failureDetail(m: import("@/physics/model").Member): string {
  const kN = (n: number) => (n / 1e3).toFixed(0);
  const mat = `${m.section.name} · ${m.material.name} (Fy ${(m.material.Fy / 1e6).toFixed(0)} MPa)`;
  if (m.failMode === "buckle") {
    const comp = Math.max(0, -m.force); // compression magnitude (N)
    const pcr =
      (Math.PI * Math.PI * m.material.E * m.section.I) / Math.pow(m.Kfactor * m.L0, 2);
    const yieldCap = m.section.A * m.material.Fy;
    const cap = compressionCapacity(m); // = min(Pcr, A·Fy)
    const governs = pcr <= yieldCap ? "Euler buckling" : "yield";
    const lambda = (m.Kfactor * m.L0) / Math.sqrt(m.section.I / m.section.A);
    return (
      `${mat}\n` +
      `Rated to ${kN(cap)} kN in compression (${governs}, λ=${lambda.toFixed(0)}).\n` +
      `Failed at ${kN(comp)} kN.`
    );
  }
  if (m.failMode === "shear") {
    // bolted END connection let go before the member yielded/buckled (doc 17).
    const cap = connectionCapacity(m);
    return (
      `${mat}\n` +
      `End connection rated to ${kN(cap)} kN (bolt shear / bearing).\n` +
      `The bolted joint sheared out at ${kN(Math.abs(m.force))} kN — the member itself was still intact.`
    );
  }
  // tension snap (or yield): A·Fy governs
  const cap = tensionCapacity(m);
  return (
    `${mat}\n` +
    `Rated to ${kN(cap)} kN in tension (A·Fy, A=${(m.section.A * 1e4).toFixed(1)} cm²).\n` +
    `Failed at ${kN(Math.max(0, m.force))} kN.`
  );
}

// --- click a member to inspect its material/section spec ---
const inspector = new PartInspector(document.getElementById("inspector")!);
inspector.hide();
const raycaster = new Raycaster();
const ndc = new Vector2();
let pointerDownX = 0;
let pointerDownY = 0;

// bright overlay that highlights the clicked member (follows it as it deforms)
const HL_UP = new Vector3(0, 1, 0);
const highlightMesh = new Mesh(
  new CylinderGeometry(1, 1, 1, 10),
  new MeshBasicMaterial({ color: 0x66e0ff, transparent: true, opacity: 0.5, depthTest: false }),
);
highlightMesh.visible = false;
highlightMesh.renderOrder = 999;
viewport.scene.add(highlightMesh);
let highlightedIdx: number | null = null;
const _ha = new Vector3();
const _hb = new Vector3();
const _hd = new Vector3();
const _hm = new Vector3();
const _hq = new Quaternion();
function updateHighlight() {
  const m = highlightedIdx != null && built ? built.structure.members[highlightedIdx] : undefined;
  if (!m || m.failed) {
    highlightMesh.visible = false;
    return;
  }
  const a = built.structure.nodes[m.i];
  const b = built.structure.nodes[m.j];
  if (!a || !b) {
    highlightMesh.visible = false;
    return;
  }
  _ha.set(a.x, a.y, a.z);
  _hb.set(b.x, b.y, b.z);
  _hd.subVectors(_hb, _ha);
  const len = _hd.length() || 1e-6;
  _hm.copy(_ha).addScaledVector(_hd, 0.5);
  _hq.setFromUnitVectors(HL_UP, _hd.multiplyScalar(1 / len));
  const r = 0.08 + m.section.dragWidth * 0.95; // a touch fatter than the member
  highlightMesh.position.copy(_hm);
  highlightMesh.quaternion.copy(_hq);
  highlightMesh.scale.set(r, len, r);
  highlightMesh.visible = true;
}

canvas.addEventListener("pointerdown", (e) => {
  pointerDownX = e.clientX;
  pointerDownY = e.clientY;
});
canvas.addEventListener("pointerup", (e) => {
  if (freeMode) return;
  if (Math.hypot(e.clientX - pointerDownX, e.clientY - pointerDownY) > 5) return; // drag = orbit, not a pick
  if (!view) return;
  const rect = canvas.getBoundingClientRect();
  ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
  ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  raycaster.setFromCamera(ndc, viewport.getCamera());
  const hit = raycaster.intersectObject(view.pickTarget).find((h) => h.instanceId != null);
  inspectMember(hit?.instanceId ?? null);
});

// Delete the highlighted member (cut a guy / remove a leg or brace) to sabotage
// the tower and watch it fall. Ignored while typing in a form field.
function isEditableTarget(el: EventTarget | null): boolean {
  const node = el as HTMLElement | null;
  return !!node && (node.tagName === "INPUT" || node.tagName === "SELECT" || node.tagName === "TEXTAREA" || node.isContentEditable);
}

function handleGlobalShortcuts(e: KeyboardEvent): void {
  const keyN = e.code === "KeyN" || e.key.toLowerCase() === "n";
  const newTowerShortcut = keyN && (
    ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey) ||
    (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.altKey)
  );
  if (newTowerShortcut && !isEditableTarget(e.target)) {
    e.preventDefault();
    openBlankTower();
    return;
  }
}

window.addEventListener("keydown", handleGlobalShortcuts, true);
document.addEventListener("keydown", handleGlobalShortcuts, true);

window.addEventListener("keydown", (e) => {
  if (e.key !== "Delete" && e.key !== "x" && e.key !== "X") return;
  if (isEditableTarget(document.activeElement)) return;
  if (freeMode) return;
  if (highlightedIdx == null || !built) return;
  severMember(highlightedIdx);
  highlightedIdx = null;
  inspector.hide();
});

// Sever the whole collinear member line through the clicked member (cutting a
// leg removes ALL its stacked segments, not just one 2.5 m panel — so a
// redundant tower actually loses a corner and topples).
function severMember(startIdx: number) {
  const { members, nodes } = built.structure;
  const dirOf = (mm: (typeof members)[number]): [number, number, number] => {
    const a = nodes[mm.i]!;
    const b = nodes[mm.j]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dz = b.z - a.z;
    const L = Math.hypot(dx, dy, dz) || 1;
    return [dx / L, dy / L, dz / L];
  };
  const start = members[startIdx];
  if (!start || start.failed) return;
  const d0 = dirOf(start);
  const queue = [startIdx];
  const seen = new Set<number>([startIdx]);
  while (queue.length) {
    const i = queue.shift()!;
    const m = members[i]!;
    m.failed = true;
    m.failMode = m.force >= 0 ? "snap" : "buckle";
    for (let j = 0; j < members.length; j++) {
      if (seen.has(j)) continue;
      const mm = members[j]!;
      if (mm.failed || mm.tensionOnly) continue;
      const shares = mm.i === m.i || mm.i === m.j || mm.j === m.i || mm.j === m.j;
      if (!shares) continue;
      const dj = dirOf(mm);
      const dot = Math.abs(d0[0] * dj[0] + d0[1] * dj[1] + d0[2] * dj[2]);
      if (dot > 0.9) {
        seen.add(j);
        queue.push(j);
      }
    }
  }
  solver.partsLost = true; // parts removed → "Get Sued"
}

// --- current simulation state ---
const designs = listDesigns();
// open on a robust self-supporting tower rather than the sensitive guyed mast
const bootDesign =
  designs.find((d) => d.id === "lattice-40m") ?? designs[0]!;

// The app now builds/tests an ARBITRARY TowerSpec (not just a registered id). We
// keep the whole TowerDesign (id/name/description/spec + optional live
// freeform structure) so save/share/export can round-trip it. Registry buttons
// set currentDesign to a registered design; editing in the designer forks it
// into a "custom" design.
let currentDesign: TowerDesign = {
  id: bootDesign.id,
  name: bootDesign.name,
  description: bootDesign.description,
  spec: bootDesign.spec,
};
let built: BuiltTower;
let solver: Solver;
let view: StructureView | null = null;
let appView: AppurtenanceView | null = null;
let realisticMode = false; // stress-view (util colors) vs realistic steel render
let schematicDrawEnabled = false;
// One DynamicsAnalyzer per built tower — computes f1, Scruton, shedding, DAF, …
// from the actual member layout and mass; rebuilt whenever the tower changes.
let analyzer: DynamicsAnalyzer;

function inspectMember(memberIndex: number | null): void {
  const member = memberIndex != null && built ? built.structure.members[memberIndex] : undefined;
  if (member && memberIndex != null) {
    highlightedIdx = memberIndex;
    schematic.setSelectedMember(memberIndex);
    inspector.show(member, {
      windSpeed: currentWind,
      iceMm: currentIceMm,
      tempC: currentTempC - TEMP_NEUTRAL,
    });
  } else {
    highlightedIdx = null;
    schematic.setSelectedMember(null);
    inspector.hide();
  }
}

schematic = new Schematic2D(document.getElementById("schematic")!, {
  onMemberClick: inspectMember,
  onDrawMember: (from, to) => {
    ensureFreeBuilder().addMemberAt(from.x, from.y, to.x, to.y);
  },
  onSelectBox: (from, to, add) => {
    ensureFreeBuilder().selectBox(from.x, from.y, to.x, to.y, add);
  },
  onSelectPoint: (point, add) => {
    ensureFreeBuilder().selectAt(point.x, point.y, add);
  },
  onMoveSelection: (delta) => {
    ensureFreeBuilder().moveSelectionBy(delta.x, delta.y);
  },
  canStartMemberAt: () => {
    const builder = ensureFreeBuilder();
    return builder.isDrawingMode();
  },
  canMoveNodeAt: (point) => {
    const builder = ensureFreeBuilder();
    // In edit mode, dragging any joint or segment moves it (grab-and-move).
    return !builder.isDrawingMode() && builder.hasGrabbableAt(point.x, point.y);
  },
  onGrabForMove: (point) => ensureFreeBuilder().grabForMove(point.x, point.y),
  isDrawEnabled: () => schematicDrawEnabled,
  isSimulating: () => simControlsVisible(),
  onPlacementMove: (x, y) => ensureFreeBuilder().placementMoveTo(x, y),
  onPlacementCommit: () => ensureFreeBuilder().placementCommit(),
  onPlacementRotate: () => ensureFreeBuilder().placementRotate(),
  onPlacementScale: (f) => ensureFreeBuilder().placementScaleBy(f),
  onPlacementCancel: () => ensureFreeBuilder().placementCancel(),
  getEditCursor: () => (ensureFreeBuilder().isDrawingMode() ? "crosshair" : "default"),
  getEditOverlay: () => freeBuilder?.getOverlay() ?? null,
});
window.addEventListener("resize", () => schematic.resize());

/** Format a USD amount compactly for tooltips ($1.2M / $340k / $920). */
function usd(n: number): string {
  return n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n)}`;
}

// --- custom hover tooltip for HUD stat breakdowns --------------------------
// Native `title` tooltips are slow/unreliable and can't be styled, so we render
// our own floating panel. One singleton element; each stat row stores its current
// breakdown text and shows it on hover (text is refreshed live for rent/payback).
let bdTip: HTMLDivElement | null = null;
const bdText = new WeakMap<HTMLElement, string>();
function ensureTip(): HTMLDivElement {
  if (bdTip) return bdTip;
  const t = document.createElement("div");
  t.id = "hud-tooltip";
  t.hidden = true;
  document.body.appendChild(t);
  bdTip = t;
  return t;
}

/** Attach a hover breakdown to a readout stat: shows a styled floating panel on
 *  hover and marks the row with a help cursor + dotted-underlined value. */
function setBreakdown(
  valueEl: HTMLElement,
  heading: string,
  lines: Array<{ label: string; value: string }>,
  footer?: string,
): void {
  const body = lines.length
    ? lines.map((l) => `${l.label}   ${l.value}`).join("\n")
    : "(nothing yet)";
  const text = `${heading}\n${"─".repeat(Math.max(heading.length, 12))}\n${body}${footer ? `\n\n${footer}` : ""}`;
  const row = (valueEl.closest(".stat") as HTMLElement | null) ?? valueEl;
  bdText.set(row, text);
  row.classList.add("has-breakdown");
  row.removeAttribute("title");

  if (row.dataset.bdBound) return; // bind hover handlers once
  row.dataset.bdBound = "1";
  const tip = ensureTip();
  const place = (e: MouseEvent) => {
    const pad = 14;
    const r = tip.getBoundingClientRect();
    // readout panel is top-right, so prefer the LEFT of the cursor; clamp to view.
    let x = e.clientX - r.width - pad;
    if (x < pad) x = e.clientX + pad;
    let y = e.clientY + pad;
    if (y + r.height > window.innerHeight - pad) y = window.innerHeight - r.height - pad;
    tip.style.left = `${Math.max(pad, x)}px`;
    tip.style.top = `${Math.max(pad, y)}px`;
  };
  row.addEventListener("mouseenter", (e) => {
    const txt = bdText.get(row);
    if (!txt) return;
    tip.textContent = txt;
    tip.hidden = false;
    place(e);
  });
  row.addEventListener("mousemove", place);
  row.addEventListener("mouseleave", () => {
    tip.hidden = true;
  });
}

function builtFromFreeform(structure: Structure): BuiltTower | null {
  if (structure.nodes.length === 0 || structure.members.length === 0) return null;

  const ys = structure.nodes.map((n) => n.y);
  const height = Math.max(...ys);
  const halfWidth = Math.max(
    1,
    ...structure.nodes.map((n) => Math.hypot(n.x, n.z)),
  );
  const baseNodes = structure.nodes
    .map((n, i) => (n.fixed ? i : -1))
    .filter((i) => i >= 0);
  const levels = new Map<string, number[]>();
  structure.nodes.forEach((n, i) => {
    const key = n.y.toFixed(3);
    const level = levels.get(key);
    if (level) level.push(i);
    else levels.set(key, [i]);
  });
  const rings = [...levels.entries()]
    .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
    .map(([, ids]) => ids);

  // Give the hand-drawn tower real foundations from the designer's Foundation &
  // Ground settings (soil / footing / pad / concrete), attaching bearing/uplift
  // capacity to its fixed base nodes — same as a compiled parametric tower.
  const foundation = resolveFoundationFor(structure, baseNodes, currentDesign.spec.foundation);
  return {
    structure,
    render: { skin: "lattice", rings, baseNodes, anchors: [] },
    type: "lattice",
    height,
    halfWidth,
    foundation,
  };
}

function currentDesignForPersistence(): TowerDesign {
  const d = forkToCustom();
  if (freeSourceActive && freeStructure) {
    // Persist the authored 2D graph (the source of a hand-drawn tower), not the
    // derived 3D structure — the graph round-trips exactly and stays editable.
    return { ...d, structure: undefined, freeGraph: ensureFreeBuilder().exportGraph() };
  }
  return d;
}

function seedFreeBuilderFromStructure(structure: Structure): void {
  const fb = ensureFreeBuilder();
  suppressFreeBuilderChange = true;
  try {
    fb.loadEditableElevation(structure);
    freeStructure = fb.getStructure();
  } finally {
    suppressFreeBuilderChange = false;
  }
}

function seedFreeBuilderFromGraph(graph: FreeGraph): void {
  const fb = ensureFreeBuilder();
  suppressFreeBuilderChange = true;
  try {
    fb.loadGraph(graph);
    freeStructure = fb.getStructure();
  } finally {
    suppressFreeBuilderChange = false;
  }
}

function clearRenderedTower(): void {
  if (view) {
    viewport.scene.remove(view.group);
    view.dispose();
    view = null;
  }
  if (appView) {
    viewport.scene.remove(appView.group);
    appView.dispose();
    appView = null;
  }
  highlightedIdx = null;
  schematic.setSelectedMember(null);
  inspector.hide();
  schematic.setStructure({ nodes: [], members: [] }, { height: 1, halfWidth: 1 });
  els.members.textContent = "0";
  els.height.textContent = "-";
  els.weight.textContent = "-";
  els.base.textContent = "-";
  els.sail.textContent = "-";
}

function fmtMass(kg: number): string {
  return kg >= 1000 ? `${(kg / 1000).toFixed(1)} t` : `${kg.toFixed(0)} kg`;
}

function updateMassAndSailReadouts(): void {
  if (!built) return;
  const m = towerMetrics(built);
  const iceT = currentIceMm / 1000;
  let appEPA = 0;
  let iceMassKg = 0;
  let iceSailM2 = 0;
  const matMass = new Map<string, number>();

  for (const node of built.structure.nodes) {
    if (node.mountedLoads) for (const { load } of node.mountedLoads) appEPA += loadEPA(load);
  }
  for (const mem of built.structure.members) {
    matMass.set(mem.material.name, (matMass.get(mem.material.name) ?? 0) + mem.section.A * mem.L0 * mem.material.rho);
    if (iceT > 0) {
      iceMassKg += Math.PI * iceT * (mem.section.dragWidth + iceT) * mem.L0 * ICE_DENSITY_KG_M3;
      iceSailM2 += 2 * iceT * mem.L0;
    }
  }
  if (iceT > 0) iceMassKg += appEPA * iceT * ICE_DENSITY_KG_M3;

  const totalMass = m.steelMassKg + iceMassKg;
  els.weight.textContent = fmtMass(totalMass);
  const massLines = [...matMass.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([name, kg]) => ({ label: name, value: fmtMass(kg) }));
  if (iceMassKg > 0) massLines.push({ label: "Ice accretion", value: fmtMass(iceMassKg) });
  setBreakdown(els.weight, "Tower mass", massLines, `Total: ${fmtMass(totalMass)}`);

  const totalSail = m.sailAreaM2 + iceSailM2 + appEPA;
  els.sail.textContent = `${totalSail.toFixed(1)} m²`;
  setBreakdown(
    els.sail,
    "Wind sail area (frontal drag)",
    [
      { label: `Dry steelwork (${m.memberCount} members)`, value: `${m.sailAreaM2.toFixed(1)} m²` },
      ...(iceSailM2 > 0 ? [{ label: "Ice on steelwork", value: `${iceSailM2.toFixed(1)} m²` }] : []),
      { label: "Appurtenances (EPA)", value: `${appEPA.toFixed(1)} m²` },
    ],
    `Total: ${totalSail.toFixed(1)} m²`,
  );
}

/** Build + (re)test the current design's spec. This is the single build path. */
function rebuild() {
  // Re-extrude a PRISTINE structure from the authored graph each build. The solver
  // mutates node positions in place, so reusing the cached freeStructure would
  // rebuild from a collapsed tower — Reset could never stand a blown-down tower.
  const freeBuilt =
    freeSourceActive && freeStructure ? builtFromFreeform(ensureFreeBuilder().getStructure()) : null;
  if (freeSourceActive && !freeBuilt) return;
  if (blankTowerActive && !freeSourceActive) {
    clearRenderedTower();
    return;
  }
  simPaused = false;
  simTime = 0;
  maxSwayM = 0; // reset the peak-sway max-hold for the new tower
  replay.reset();
  eventLog.reset();
  if (view) {
    viewport.scene.remove(view.group);
    view.dispose();
    view = null;
  }
  if (appView) {
    viewport.scene.remove(appView.group);
    appView.dispose();
    appView = null;
  }
  inspector.hide();
  highlightedIdx = null;
  schematic.setSelectedMember(null);
  designer?.setDrawnMode(freeSourceActive); // hand-drawn → hide parametric geometry controls
  built = freeBuilt ?? compileTower(currentDesign.spec, groundHeight);
  solver = new Solver(built.structure, {
    windSpeed: currentWind,
    windDir: currentWindDir, // heading the wind blows toward (radians)
    groundHeight,
    iceThickness: currentIceMm / 1000,
    temperature: currentTempC - TEMP_NEUTRAL,
    corrosion: ageToCorrosion(currentAgeYr), // section loss from aging
    gustiness: gusty ? DURST_IU : 0, // turbulent buffeting when "Gusts" is on
  });
  // Serviceability observer: top drift, twist/sway, microwave-link soft-fail
  // (docs/engineering/25). Reference geometry is captured from the undeformed
  // build rings here, before the tower is stepped.
  solver.initServiceability(built.render.rings, built.height);
  analyzer = new DynamicsAnalyzer(built, { zeta: STEEL_DAMPING });
  view = new StructureView(built);
  viewport.scene.add(view.group);
  appView = new AppurtenanceView(built);
  viewport.scene.add(appView.group);
  view.setRealistic(realisticMode);
  view.setIce(currentIceMm / 1000);
  viewport.frameTower(built.height, built.halfWidth);
  windView.setTower(built.height, built.halfWidth);
  schematic.setStructure(built.structure, {
    height: built.height,
    halfWidth: built.halfWidth,
    foundation: built.foundation,
  });

  const m = towerMetrics(built);
  els.members.textContent = String(m.memberCount);
  els.height.textContent = `${m.height.toFixed(0)} m`;
  els.base.textContent = `${m.baseWidthM.toFixed(1)} m`;
  updateMassAndSailReadouts();

  // --- hover breakdowns (native tooltips) on the composed stats -------------
  const cb = estimateCost(built);
  const cost = cb.total;
  els.cost.textContent = cost >= 1e6 ? `$${(cost / 1e6).toFixed(2)}M` : `$${Math.round(cost / 1000)}k`;
  setBreakdown(
    els.cost,
    "Build cost",
    cb.lines.map((l) => ({ label: l.label, value: usd(l.usd) })),
    `Total: ${usd(cb.total)}`,
  );

  // Payback mechanic — capital cost from the full potential rent; the live rent
  // (which drops when a microwave link goes down) is updated each frame.
  currentEcon = towerEconomics(built);
  updateEconomicsHud();
}

let currentEcon: ReturnType<typeof towerEconomics> | null = null;
/** Rent + payback readout, recomputed live: a dropped microwave link takes the
 *  site off-air → no rent (the more valuable, higher-up gear was priced in already). */
function updateEconomicsHud() {
  const e = currentEcon;
  if (!e) return;
  if (e.monthlyRevenue <= 0) {
    els.rent.textContent = "no equipment";
    els.rent.className = "";
    els.payback.textContent = "—";
    els.payback.className = "";
    return;
  }
  let liveRent = e.monthlyRevenue;
  let offAir = false;
  const svc = solver?.serviceability;
  if (svc && svc.dishes.length > 0) {
    if (svc.worstLink === "lost") {
      liveRent = 0;
      offAir = true;
    } else if (svc.worstLink === "degraded") {
      liveRent *= 0.5;
    }
  }
  els.rent.textContent = offAir ? "$0 · link down" : `$${Math.round(liveRent).toLocaleString()}/mo`;
  els.rent.className = offAir ? "fail" : svc?.worstLink === "degraded" ? "warn" : "";
  // Rent breakdown: per-equipment lease (potential), with the live-status note.
  if (built) {
    const lease = leaseBreakdown(built);
    const lines = lease.map((l) => ({ label: l.label, value: `${usd(l.usd)}/mo` }));
    const note = offAir
      ? "LINK DOWN → $0 live (site off-air)"
      : svc?.worstLink === "degraded"
        ? "degraded link → ×0.5 live"
        : `Live: ${usd(liveRent)}/mo`;
    setBreakdown(els.rent, "Monthly rent (potential, per unit)", lines, `${note}\nHigher mounts earn more.`);
  }
  if (liveRent <= 0) {
    els.payback.textContent = "off-air";
    els.payback.className = "fail";
    setBreakdown(els.payback, "Payback", [{ label: "Live rent", value: "$0 (off-air)" }],
      `Build cost ${usd(e.capitalCost)} never recovered while off-air.`);
  } else {
    const months = e.capitalCost / liveRent;
    const yrs = months / 12;
    els.payback.textContent = months < 24 ? `${months.toFixed(0)} mo` : `${yrs.toFixed(1)} yr`;
    els.payback.className = yrs <= 3 ? "ok" : yrs <= 8 ? "warn" : "fail";
    setBreakdown(els.payback, "Payback period", [
      { label: "Build cost", value: usd(e.capitalCost) },
      { label: "Live rent", value: `${usd(liveRent)}/mo` },
    ], `${usd(e.capitalCost)} ÷ ${usd(liveRent)}/mo = ${months.toFixed(0)} months`);
  }
}

/** Load a full design (from registry pick, save, import, share) and rebuild. */
function loadDesign(d: TowerDesign, opts: { markActiveTower?: boolean } = {}) {
  closeFreeDraw();
  currentDesign = { ...d };
  if (d.freeGraph) {
    seedFreeBuilderFromGraph(d.freeGraph);
    freeSourceActive = true;
    blankTowerActive = false;
  } else if (d.structure) {
    seedFreeBuilderFromStructure(d.structure); // legacy pre-freeGraph files
    freeSourceActive = true;
    blankTowerActive = false;
  } else {
    setActiveModelFromSpec(currentDesign.spec);
  }
  setWind(0);
  stopRamp();
  rebuild();
  // keep the tower button row in sync (highlight matching registered id, else none)
  if (opts.markActiveTower !== false) {
    els.typeRow.querySelectorAll(".seg").forEach((x) =>
      x.classList.toggle("active", (x as HTMLElement).dataset.id === d.id),
    );
  }
  // if the designer is open, reseed it with the loaded spec
  designer?.setSpec(d.spec);
}

// --- UI elements ---
const els = {
  typeRow: document.getElementById("type-row")!,
  wind: document.getElementById("wind") as HTMLInputElement,
  windVal: document.getElementById("wind-val")!,
  ice: document.getElementById("ice") as HTMLInputElement,
  iceVal: document.getElementById("ice-val")!,
  temp: document.getElementById("temp") as HTMLInputElement,
  tempVal: document.getElementById("temp-val")!,
  gFall: document.getElementById("g-fall")!,
  gSued: document.getElementById("g-sued")!,
  status: document.getElementById("r-status")!,
  util: document.getElementById("r-util")!,
  found: document.getElementById("r-found")!,
  fatigue: document.getElementById("r-fatigue")!,
  height: document.getElementById("r-height")!,
  weight: document.getElementById("r-weight")!,
  cost: document.getElementById("r-cost")!,
  rent: document.getElementById("r-rent")!,
  payback: document.getElementById("r-payback")!,
  sail: document.getElementById("r-sail")!,
  base: document.getElementById("r-base")!,
  q: document.getElementById("r-q")!,
  members: document.getElementById("r-members")!,
  drift: document.getElementById("r-drift")!,
  sway: document.getElementById("r-sway")!,
  link: document.getElementById("r-link")!,
  reset: document.getElementById("reset") as HTMLButtonElement,
  ramp: document.getElementById("ramp") as HTMLButtonElement,
};

let currentWind = 0;
let currentWindDir = 0; // radians — compass heading the wind blows toward
let currentIceMm = 0; // radial ice thickness (mm)
let ramping = false;
let gusty = false; // turbulent gusts (buffet the tower) vs a steady mean wind

function setWind(v: number) {
  currentWind = Math.max(0, Math.min(120, v));
  if (solver) solver.params.windSpeed = currentWind;
  els.wind.value = String(Math.round(currentWind));
  els.windVal.textContent = `${Math.round(currentWind)} m/s · ${Math.round(currentWind * 3.6)} km/h`;
}

// --- wind-flow visualization + gust toggles ---
const windVizBtn = document.getElementById("wind-viz") as HTMLButtonElement;
let windVizOn = true;
windVizBtn.addEventListener("click", () => {
  windVizOn = !windVizOn;
  windView.setVisible(windVizOn);
  windVizBtn.classList.toggle("active", windVizOn);
});

const gustsBtn = document.getElementById("gusts") as HTMLButtonElement;
gustsBtn.addEventListener("click", () => {
  gusty = !gusty;
  if (solver) solver.params.gustiness = gusty ? DURST_IU : 0;
  gustsBtn.classList.toggle("active", gusty);
});

// --- wind direction dial (0–359°): swings the load AND the flow visualization ---
const windDirSlider = document.getElementById("wind-dir") as HTMLInputElement;
const windDirVal = document.getElementById("wind-dir-val")!;
const COMPASS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
function setWindDir(deg: number) {
  const d = ((deg % 360) + 360) % 360;
  currentWindDir = (d * Math.PI) / 180;
  if (solver) solver.params.windDir = currentWindDir; // wake + drag swing to match
  windDirSlider.value = String(Math.round(d));
  windDirVal.textContent = `${Math.round(d)}° ${COMPASS[Math.round(d / 45) % 8] ?? "N"}`;
}
windDirSlider.addEventListener("input", () => setWindDir(parseFloat(windDirSlider.value)));

function setIce(mm: number) {
  currentIceMm = Math.max(0, Math.min(75, mm));
  if (solver) solver.params.iceThickness = currentIceMm / 1000; // mm → m
  view?.setIce(currentIceMm / 1000);
  els.ice.value = String(Math.round(currentIceMm));
  els.iceVal.textContent = `${Math.round(currentIceMm)} mm`;
  updateMassAndSailReadouts();
}
els.ice.addEventListener("input", () => setIce(parseFloat(els.ice.value)));

const TEMP_NEUTRAL = 15; // install temperature (°C) → ΔT = 0
let currentTempC = TEMP_NEUTRAL;
function setTemp(c: number) {
  currentTempC = Math.max(-40, Math.min(50, c));
  if (solver) solver.params.temperature = currentTempC - TEMP_NEUTRAL; // ΔT
  els.temp.value = String(Math.round(currentTempC));
  els.tempVal.textContent = `${Math.round(currentTempC)} °C`;
}
els.temp.addEventListener("input", () => setTemp(parseFloat(els.temp.value)));

// --- corrosion / aging: galvanizing protects ~20 yr, then section loss (doc 20) ---
const ageSlider = document.getElementById("age") as HTMLInputElement;
const ageVal = document.getElementById("age-val")!;
let currentAgeYr = 0;
function ageToCorrosion(yr: number): number {
  return Math.max(0, Math.min(0.35, (yr - 20) * 0.008)); // ≈0.8%/yr after 20 yr, cap 35%
}
function setAge(yr: number) {
  currentAgeYr = Math.max(0, Math.min(60, yr));
  const c = ageToCorrosion(currentAgeYr);
  if (solver) solver.params.corrosion = c;
  ageSlider.value = String(Math.round(currentAgeYr));
  ageVal.textContent = currentAgeYr < 1 ? "new" : `${Math.round(currentAgeYr)} yr · −${Math.round(c * 100)}%`;
}
ageSlider.addEventListener("input", () => setAge(parseFloat(ageSlider.value)));

function stopRamp() {
  ramping = false;
  els.ramp.classList.remove("on");
}

function resetWeather(): void {
  stopRamp();
  presetSel.value = "";
  gusty = false;
  gustsBtn.classList.remove("active");
  setWind(0);
  setWindDir(0);
  setIce(0);
  setTemp(TEMP_NEUTRAL);
  setAge(0);
}

function setGoal(el: HTMLElement, ok: boolean) {
  el.classList.toggle("ok", ok);
  el.classList.toggle("fail", !ok);
  const mark = el.querySelector(".mark");
  if (mark) mark.textContent = ok ? "✓" : "✗";
}

// Build the tower selector from the registry. To keep the picker uncluttered we
// only show the generic ARCHETYPES up front (guyed / lattice / monopole variants —
// the ones with no `author`); the 40+ real-world & reference towers (which all
// carry an `author` citation) live behind an "Example towers" expander.
function makeTowerBtn(d: TowerDesign): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = "seg";
  b.textContent = d.name;
  b.title = d.description;
  b.dataset.id = d.id;
  if (d.id === currentDesign.id) b.classList.add("active");
  b.addEventListener("click", () => {
    const reg = getDesign(d.id)!;
    loadDesign({ id: reg.id, name: reg.name, description: reg.description, spec: reg.spec });
  });
  return b;
}

// The tower ROW shows only the generic archetypes; the 40+ real-world & reference
// towers live in the "Example Towers" nav-bar menu (openExamples) — a vertical,
// grouped, searchable list instead of an endless horizontal scroll.
// Use the real Ashland monopole as the default "Monopole" archetype instead of the
// generic 30 m one (swap it into the core bar, drop it from the examples list).
const DEFAULT_MONOPOLE_ID = "monopole-ashland-149ft";
const coreDesigns = designs
  .filter((d) => !d.author && d.id !== "monopole-40m-carrier")
  .map((d) => (d.id === "monopole-30m" ? (getDesign(DEFAULT_MONOPOLE_ID) ?? d) : d));
const exampleDesigns = designs.filter((d) => d.author && d.id !== DEFAULT_MONOPOLE_ID);
for (const d of coreDesigns) els.typeRow.appendChild(makeTowerBtn(d));

// Wind presets: real-world winds (breeze → Cat 5 → record gusts) fill the slider.
const presetSel = document.getElementById("wind-preset") as HTMLSelectElement;
for (const p of WIND_PRESETS) {
  const o = document.createElement("option");
  o.value = String(p.gust);
  o.textContent = `${p.name} · ${p.gust} m/s`;
  o.title = p.description;
  presetSel.appendChild(o);
}
presetSel.addEventListener("change", () => {
  const g = parseFloat(presetSel.value);
  if (!Number.isNaN(g)) {
    stopRamp();
    setWind(g);
  }
});

els.wind.addEventListener("input", () => {
  stopRamp();
  presetSel.value = ""; // manual override clears the preset selection
  setWind(parseFloat(els.wind.value));
});
els.reset.addEventListener("click", () => {
  resetWeather();
  rebuild();
});
els.ramp.addEventListener("click", () => {
  ramping = !ramping;
  els.ramp.classList.toggle("on", ramping);
});

// View toggle: picture-in-picture swap. One view fills the main area, the other
// sits in the bottom-right window; control/readout panels stay above both.
const schEl = document.getElementById("schematic")!;
function setMainView(elevationMain: boolean) {
  schematic.setExpanded(false); // clear any internal fullscreen inline styles
  canvas.classList.toggle("view-main", !elevationMain);
  canvas.classList.toggle("view-pip", elevationMain);
  schEl.classList.toggle("view-main", elevationMain);
  schEl.classList.toggle("view-pip", !elevationMain);
  schematic.setMainMode(elevationMain);
  viewport.setView("perspective");
  document.querySelectorAll<HTMLButtonElement>("#view-row .seg").forEach((b) =>
    b.classList.toggle("active", (b.dataset.view === "elevation") === elevationMain),
  );
  schematic.resize();
  if (elevationMain) schematic.focus();
}
document.querySelectorAll<HTMLButtonElement>("#view-row .seg").forEach((b) => {
  b.addEventListener("click", () => {
    setMainView(b.dataset.view === "elevation");
  });
});
document.querySelectorAll<HTMLButtonElement>("#projection-row .seg").forEach((b) => {
  b.addEventListener("click", () => {
    const projection = b.dataset.projection;
    if (projection !== "front" && projection !== "side" && projection !== "top") return;
    schematic.setProjection(projection);
    document.querySelectorAll("#projection-row .seg").forEach((x) => x.classList.remove("active"));
    b.classList.add("active");
    setMainView(true);
  });
});
setMainView(false); // start with 3D main, elevation in the pip

// Render toggle: utilization "stress view" (green→red) vs realistic galvanised steel
document.querySelectorAll<HTMLButtonElement>("#render-row .seg").forEach((b) => {
  b.addEventListener("click", () => {
    document.querySelectorAll("#render-row .seg").forEach((x) => x.classList.remove("active"));
    b.classList.add("active");
    realisticMode = b.dataset.render === "realistic";
    view?.setRealistic(realisticMode);
  });
});

// ===========================================================================
//  DESIGNER / PERSISTENCE / CHALLENGES / TUTORIAL WIRING
// ===========================================================================

const toolEls = {
  design: document.getElementById("tb-design") as HTMLButtonElement,
  simulate: document.getElementById("tb-simulate") as HTMLButtonElement,
  challenges: document.getElementById("tb-challenges") as HTMLButtonElement,
  assets: document.getElementById("tb-assets") as HTMLButtonElement,
  assetDrawer: document.getElementById("asset-drawer") as HTMLDivElement,
  assetClose: document.getElementById("asset-close") as HTMLButtonElement,
  blank: document.getElementById("tb-blank") as HTMLButtonElement,
  examples: document.getElementById("tb-examples") as HTMLButtonElement,
  save: document.getElementById("tb-save") as HTMLButtonElement,
  mine: document.getElementById("tb-mine") as HTMLButtonElement,
  export: document.getElementById("tb-export") as HTMLButtonElement,
  import: document.getElementById("tb-import") as HTMLButtonElement,
  share: document.getElementById("tb-share") as HTMLButtonElement,
  help: document.getElementById("tb-help") as HTMLButtonElement,
  side: document.getElementById("side-panel") as HTMLDivElement,
  sideTitle: document.getElementById("side-title")!,
  sideBody: document.getElementById("side-body")!,
  sideClose: document.getElementById("side-close") as HTMLButtonElement,
  challPanel: document.getElementById("chall-panel") as HTMLDivElement,
  challBody: document.getElementById("chall-body")!,
  challClose: document.getElementById("chall-close") as HTMLButtonElement,
  minePanel: document.getElementById("mine-panel") as HTMLDivElement,
  mineList: document.getElementById("mine-list")!,
  mineClose: document.getElementById("mine-close") as HTMLButtonElement,
  toast: document.getElementById("toast") as HTMLDivElement,
};

let toastTimer: number | undefined;
function toast(msg: string, ms = 3200) {
  toolEls.toast.textContent = msg;
  toolEls.toast.hidden = false;
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (toolEls.toast.hidden = true), ms);
}

/** Ensure the current design is a "custom" fork before we let the user edit it,
 *  so saving/sharing never clobbers a registered id. Returns the design. */
function forkToCustom(): TowerDesign {
  const isRegistered = !!getDesign(currentDesign.id);
  if (isRegistered) {
    const base = currentDesign.name.replace(/^My\s+/, "");
    currentDesign = {
      id: `custom-${Date.now().toString(36)}`,
      name: `My ${base}`,
      description: `Custom design forked from ${currentDesign.name}`,
      spec: currentDesign.spec,
      structure: currentDesign.structure,
    };
    // no registered id matches now → clear tower-button highlight
    els.typeRow.querySelectorAll(".seg").forEach((x) => x.classList.remove("active"));
  }
  return currentDesign;
}

// ---- side panel plumbing (hosts designer OR challenges, one at a time) ----
// The left panel hosts the Designer OR the Example browser (one at a time).
// Challenges live in their OWN panel (below) so they can be open ALONGSIDE either.
type SideMode = "designer" | "examples" | null;
let sideMode: SideMode = null;
let designer: TowerDesigner | null = null;
let challengePanel: ChallengePanel | null = null;
let activeChallenge: Challenge | null = null;
let rebuildTimer: number | undefined;
let freeBuilder: FreeBuilder | null = null;
let freeMode = false;
let freeSourceActive = false;
let freeStructure: Structure | null = null;
let blankTowerActive = false;
let preFreeViewMode: ViewMode = "perspective";
let preFreeElevationMain = false;
let suppressFreeBuilderChange = false;

function closeSide() {
  toolEls.side.hidden = true;
  toolEls.sideBody.replaceChildren();
  if (designer) {
    designer.dispose();
    designer = null;
  }
  sideMode = null;
  toolEls.design.classList.remove("active");
  toolEls.examples.classList.remove("active");
}

function ensureFreeBuilder(): FreeBuilder {
  if (!freeBuilder) {
    freeBuilder = new FreeBuilder({
      scene: viewport.scene,
      getCamera: () => viewport.getCamera(),
      getFaces: () => currentDesign.spec.segments?.faces ?? currentDesign.spec.stack?.faces ?? 4,
      setFaces: (faces) => {
        forkToCustom();
        if (currentDesign.spec.segments) currentDesign.spec.segments.faces = faces;
        else {
          currentDesign.spec.stack ??= {
            faces,
            panels: 12,
            baseRadius: 2,
            topRadius: 0.8,
            legSection: "legHeavy",
            braceSection: "braceMed",
            bracing: "X",
          };
          currentDesign.spec.stack.faces = faces;
        }
        designer?.setSpec(currentDesign.spec);
      },
      domElement: document.getElementById("app")!,
      container: document.getElementById("app")!,
      onChange: (s) => {
        freeStructure = s;
        if (suppressFreeBuilderChange) return;
        freeSourceActive = s.nodes.length > 0 && s.members.length > 0;
        blankTowerActive = !freeSourceActive;
        if (!freeSourceActive) freeStructure = null;
        console.info("[tower-build]", "freeform changed", {
          freeSourceActive,
          nodes: s.nodes.length,
          members: s.members.length,
          fixedNodes: s.nodes.filter((n) => n.fixed).length,
        });
        if (rebuildTimer) clearTimeout(rebuildTimer);
        rebuildTimer = window.setTimeout(() => rebuild(), 80);
      },
    });
  }
  return freeBuilder;
}

function seedFreeBuilderFromSpec(spec: TowerSpec): void {
  const fb = ensureFreeBuilder();
  suppressFreeBuilderChange = true;
  try {
    fb.loadEditableElevation(compileTower(spec, groundHeight).structure);
    freeStructure = fb.getStructure();
  } finally {
    suppressFreeBuilderChange = false;
  }
}

function setActiveModelFromSpec(spec: TowerSpec): void {
  seedFreeBuilderFromSpec(spec);
  freeSourceActive = false;
  blankTowerActive = false;
}

function closeFreeDraw(restoreView = true) {
  if (!freeMode) return;
  freeMode = false;
  schematicDrawEnabled = false;
  freeBuilder?.disable();
  if (restoreView) {
    viewport.setView(preFreeViewMode);
    setMainView(preFreeElevationMain);
  }
}

function openDrawPane(): void {
  if (freeMode) return;
  preFreeViewMode = viewport.getView();
  preFreeElevationMain = schEl.classList.contains("view-main");
  setMainView(true);
  freeMode = true;
  schematicDrawEnabled = true;
  els.typeRow.querySelectorAll(".seg").forEach((x) => x.classList.remove("active"));
  freeBuilder = ensureFreeBuilder();
  if (!freeStructure && !blankTowerActive) {
    seedFreeBuilderFromSpec(currentDesign.spec);
  }
  freeBuilder.enable();
  const current = freeBuilder.getStructure();
  if (current.nodes.length > 0 && current.members.length > 0) {
    freeStructure = current;
  }
}

function setSimControlsVisible(visible: boolean): void {
  const controls = document.getElementById("controls") as HTMLDivElement | null;
  if (!controls) return;
  controls.hidden = !visible;
  toolEls.simulate.classList.toggle("active", !controls.hidden);
  positionSidePanels();
}

function simControlsVisible(): boolean {
  return !(document.getElementById("controls") as HTMLDivElement | null)?.hidden;
}

function toggleSimControls() {
  setSimControlsVisible(!simControlsVisible());
}

function mountBuildControls(): void {
  const details = document.createElement("details");
  details.open = true;
  details.style.cssText =
    "border:1px solid #1c2530;border-radius:8px;padding:10px 12px;margin:0 12px 12px;background:#0d1219;";
  const summary = document.createElement("summary");
  summary.textContent = "Build";
  summary.style.cssText =
    "font-size:11px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:#7dd3fc;margin:0 0 8px;cursor:pointer;";
  const host = document.createElement("div");
  details.append(summary, host);
  toolEls.sideBody.appendChild(details);
  ensureFreeBuilder().mountToolbar(host, false);
}

function openDesigner() {
  if (sideMode === "designer") {
    closeSide();
    closeFreeDraw();
    return;
  }
  setMainView(true);
  closeSide();
  openDrawPane();
  sideMode = "designer";
  toolEls.side.hidden = false;
  positionSidePanels();
  toolEls.sideTitle.textContent = "Tower Designer";
  toolEls.design.classList.add("active");
  setSimControlsVisible(false);
  // Entering design mode clears the weather and resets the tower to its pristine,
  // undeformed state — you design a fresh tower, not a wind-blown / collapsed one.
  resetWeather();
  rebuild();
  let ignoreInitialEmit = true;
  designer = new TowerDesigner(toolEls.sideBody, {
    initial: currentDesign.spec,
    onChange: (spec: TowerSpec) => {
      if (ignoreInitialEmit) return;
      // fork on first edit so we never mutate a registered design in place
      forkToCustom();
      currentDesign.spec = spec;
      // If the user has drawn their own tower, spec-parameter edits (leg material,
      // foundation, parametric geometry, …) must NOT regenerate-and-replace the
      // drawing — that was silently blowing away their hand-built structure.
      // Keep the free-drawn structure as the active source; only record the spec.
      if (!(freeSourceActive && freeStructure)) {
        setActiveModelFromSpec(spec);
      }
      // debounce the live rebuild so dragging a slider isn't janky
      if (rebuildTimer) clearTimeout(rebuildTimer);
      rebuildTimer = window.setTimeout(() => rebuild(), 150);
    },
  });
  ignoreInitialEmit = false;
  designer.setDrawnMode(freeSourceActive);
  mountBuildControls();
  maybeShowTutorial();
}

// Challenges: independent panel — can be open at the same time as the Designer.
function closeChallenges() {
  toolEls.challPanel.hidden = true;
  toolEls.challBody.replaceChildren();
  if (challengePanel) {
    challengePanel.dispose();
    challengePanel = null;
  }
  toolEls.challenges.classList.remove("active");
}

function openChallenges() {
  if (!toolEls.challPanel.hidden) {
    closeChallenges();
    return;
  }
  toolEls.challPanel.hidden = false;
  positionSidePanels();
  toolEls.challenges.classList.add("active");

  challengePanel = new ChallengePanel(toolEls.challBody, {
    onSelect: (ch) => {
      activeChallenge = ch;
    },
  });

  // a "Test current tower" action beneath the panel
  const testBtn = document.createElement("button");
  testBtn.className = "btn";
  testBtn.textContent = "Test current tower";
  testBtn.style.cssText = "width:100%;margin-top:10px;";
  testBtn.addEventListener("click", () => {
    if (!activeChallenge) {
      toast("Pick a challenge first.");
      return;
    }
    const b = freeSourceActive && freeStructure
      ? (builtFromFreeform(ensureFreeBuilder().getStructure()) ?? compileTower(currentDesign.spec, groundHeight))
      : compileTower(currentDesign.spec, groundHeight);
    const result = evaluateChallenge(activeChallenge, b);
    challengePanel!.showResult(activeChallenge, result);
    toast(result.pass ? `PASS — ${activeChallenge.name}` : `FAIL — ${activeChallenge.name}`);
  });
  toolEls.challBody.appendChild(testBtn);

  const hint = document.createElement("div");
  hint.textContent =
    "Select a challenge, adjust your tower in the Designer (open it alongside this), then Test. The tower is run headless: wind is ramped until it collapses.";
  hint.style.cssText = "font-size:11px;color:#6b7d91;margin-top:8px;line-height:1.4;";
  toolEls.challBody.appendChild(hint);
}

/** Example-tower browser: a vertical, grouped, searchable list in the side panel
 *  (replaces the endless horizontal scroll of 40+ towers in the tower row). */
function openExamples() {
  closeFreeDraw();
  closeAssetDrawer();
  if (sideMode === "examples") {
    closeSide();
    return;
  }
  closeSide();
  sideMode = "examples";
  toolEls.side.hidden = false;
  positionSidePanels();
  toolEls.sideTitle.textContent = "Example Towers";
  toolEls.examples.classList.add("active");

  const categoryOf = (d: TowerDesign): string => {
    if (d.author?.startsWith("iconic")) return "Landmarks & iconic";
    if (d.id.startsWith("guyed")) return "Guyed masts";
    if (d.id.startsWith("monopole")) return "Monopoles";
    if (d.id.startsWith("selfsupport")) return "Self-supporting";
    if (d.id.startsWith("lattice")) return "Lattice towers";
    return "Other";
  };
  const ORDER = ["Guyed masts", "Lattice towers", "Monopoles", "Self-supporting", "Landmarks & iconic", "Other"];

  const wrap = document.createElement("div");
  wrap.className = "examples-menu";
  const search = document.createElement("input");
  search.type = "search";
  search.className = "examples-search";
  search.placeholder = `Search ${exampleDesigns.length} towers…`;
  const listEl = document.createElement("div");
  listEl.className = "examples-list";
  wrap.append(search, listEl);

  const render = (filter: string) => {
    listEl.replaceChildren();
    const f = filter.trim().toLowerCase();
    const groups = new Map<string, TowerDesign[]>();
    for (const d of exampleDesigns) {
      if (f && !(`${d.name} ${d.description}`.toLowerCase().includes(f))) continue;
      const cat = categoryOf(d);
      let arr = groups.get(cat);
      if (!arr) groups.set(cat, (arr = []));
      arr.push(d);
    }
    for (const cat of ORDER) {
      const items = groups.get(cat);
      if (!items?.length) continue;
      const h = document.createElement("div");
      h.className = "examples-group";
      h.textContent = `${cat} (${items.length})`;
      listEl.appendChild(h);
      for (const d of items) {
        const row = document.createElement("button");
        row.className = "examples-row";
        if (d.id === currentDesign.id) row.classList.add("active");
        row.title = d.description;
        const name = document.createElement("span");
        name.className = "ex-name";
        name.textContent = d.name;
        const desc = document.createElement("span");
        desc.className = "ex-desc";
        desc.textContent = d.description;
        row.append(name, desc);
        row.addEventListener("click", () => {
          const reg = getDesign(d.id)!;
          loadDesign({ id: reg.id, name: reg.name, description: reg.description, spec: reg.spec });
          closeSide();
        });
        listEl.appendChild(row);
      }
    }
    if (!listEl.children.length) {
      const none = document.createElement("div");
      none.className = "examples-none";
      none.textContent = "No towers match.";
      listEl.appendChild(none);
    }
  };
  search.addEventListener("input", () => render(search.value));
  render("");
  toolEls.sideBody.appendChild(wrap);
  search.focus();
}

function closeAssetDrawer() {
  toolEls.assetDrawer.hidden = true;
  toolEls.assets.classList.remove("active");
}

function toggleAssetDrawer() {
  const open = toolEls.assetDrawer.hidden;
  toolEls.assetDrawer.hidden = !open;
  toolEls.assets.classList.toggle("active", open);
}

function openBlankTower() {
  closeAssetDrawer();
  closeSide();
  closeFreeDraw(false);
  if (rebuildTimer) {
    clearTimeout(rebuildTimer);
    rebuildTimer = undefined;
  }
  freeBuilder = ensureFreeBuilder();
  blankTowerActive = true;
  freeSourceActive = false;
  freeStructure = null;
  freeBuilder.clear();
  freeStructure = null;
  freeSourceActive = false;
  clearRenderedTower();
  openDesigner();
  toast("Blank tower: draw members in the elevation view.");
}

toolEls.design.addEventListener("click", openDesigner);
toolEls.simulate.addEventListener("click", toggleSimControls);
toolEls.challenges.addEventListener("click", openChallenges);
toolEls.challClose.addEventListener("click", closeChallenges);
toolEls.assets.addEventListener("click", toggleAssetDrawer);
toolEls.assetClose.addEventListener("click", closeAssetDrawer);
toolEls.blank.addEventListener("click", openBlankTower);
toolEls.blank.title = "New blank tower (Alt+N)";
toolEls.simulate.classList.toggle("active", !(document.getElementById("controls") as HTMLDivElement).hidden);

// Keep the Designer / Challenges panels ABOVE the bottom-left control bar so they
// never cover the wind / ice / etc. sliders; they also size to their content.
function positionSidePanels() {
  const controls = document.getElementById("controls");
  const top = parseFloat(toolEls.side.style.top || "") || toolEls.side.getBoundingClientRect().top || 18;
  const controlsVisible = !!controls && !controls.hidden && controls.offsetParent !== null;
  const bottomLimit = controlsVisible ? controls.getBoundingClientRect().top - 12 : window.innerHeight - 18;
  const avail = bottomLimit - top;
  const mh = `${Math.max(360, avail)}px`;
  toolEls.side.style.maxHeight = mh;
  toolEls.side.style.height = mh;
  toolEls.challPanel.style.maxHeight = mh;
  toolEls.challPanel.style.height = mh;
}
window.addEventListener("resize", positionSidePanels);

/**
 * Make a floating panel draggable by its `.side-head` header. Sets inline
 * left/top (overriding the CSS dock position) and clamps to the viewport. Clicks
 * on the close button (or any button in the header) don't start a drag.
 */
function makeDraggable(panel: HTMLElement): void {
  const handle = panel.querySelector(".side-head") as HTMLElement | null;
  if (!handle) return;
  handle.style.cursor = "grab";
  handle.style.userSelect = "none";
  let startX = 0;
  let startY = 0;
  let baseLeft = 0;
  let baseTop = 0;
  const onMove = (e: PointerEvent) => {
    const w = panel.offsetWidth;
    let left = baseLeft + (e.clientX - startX);
    let top = baseTop + (e.clientY - startY);
    left = Math.max(0, Math.min(window.innerWidth - Math.min(w, 80), left));
    top = Math.max(0, Math.min(window.innerHeight - 40, top));
    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
    panel.style.right = "auto";
    panel.style.bottom = "auto";
  };
  const onUp = (e: PointerEvent) => {
    handle.style.cursor = "grab";
    handle.releasePointerCapture?.(e.pointerId);
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
  };
  handle.addEventListener("pointerdown", (e) => {
    if ((e.target as HTMLElement).closest("button")) return; // let the ✕ close work
    const r = panel.getBoundingClientRect();
    baseLeft = r.left;
    baseTop = r.top;
    startX = e.clientX;
    startY = e.clientY;
    handle.style.cursor = "grabbing";
    handle.setPointerCapture?.(e.pointerId);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    e.preventDefault();
  });
}
makeDraggable(toolEls.side);
makeDraggable(toolEls.challPanel);
makeDraggable(toolEls.assetDrawer);

toolEls.examples.addEventListener("click", openExamples);
toolEls.sideClose.addEventListener("click", closeSide);

// ---- save / my towers / export / import / share ----
toolEls.save.addEventListener("click", () => {
  // saving a registered tower forks it to a custom design first
  const wasRegistered = !!getDesign(currentDesign.id);
  const d = forkToCustom();
  if (wasRegistered || !d.name) {
    const name = window.prompt("Name this design:", d.name || "My Tower");
    if (name === null) return; // cancelled
    d.name = name.trim() || d.name;
  }
  saveLocal(d);
  toast(`Saved "${d.name}".`);
});

function renderMineList() {
  const saved = listLocal();
  toolEls.mineList.replaceChildren();
  if (saved.length === 0) {
    const empty = document.createElement("div");
    empty.className = "mine-empty";
    empty.textContent = "No saved designs yet. Design a tower, then Save.";
    toolEls.mineList.appendChild(empty);
    return;
  }
  for (const d of saved) {
    const row = document.createElement("div");
    row.className = "mine-row";
    const load = document.createElement("button");
    load.className = "mine-load";
    load.textContent = d.name;
    load.title = d.description;
    load.addEventListener("click", () => {
      loadDesign(d);
      toolEls.minePanel.hidden = true;
      toast(`Loaded "${d.name}".`);
    });
    const del = document.createElement("button");
    del.className = "mine-del";
    del.textContent = "Delete";
    del.addEventListener("click", () => {
      if (window.confirm(`Delete "${d.name}"?`)) {
        deleteLocal(d.id);
        renderMineList();
      }
    });
    row.appendChild(load);
    row.appendChild(del);
    toolEls.mineList.appendChild(row);
  }
}

toolEls.mine.addEventListener("click", () => {
  if (!toolEls.minePanel.hidden) {
    toolEls.minePanel.hidden = true;
    return;
  }
  renderMineList();
  toolEls.minePanel.hidden = false;
});
toolEls.mineClose.addEventListener("click", () => (toolEls.minePanel.hidden = true));

toolEls.export.addEventListener("click", () => {
  const d = currentDesignForPersistence();
  exportFile(d);
  toast(`Exported "${d.name}" as ${d.id}.json.`);
});

toolEls.import.addEventListener("click", async () => {
  const { design, error } = await importFile();
  if (error) {
    toast(`Import failed: ${error}`);
    return;
  }
  if (design) {
    loadDesign(design);
    toast(`Imported "${design.name}".`);
  }
});

toolEls.share.addEventListener("click", async () => {
  const d = currentDesignForPersistence();
  const url = encodeShareUrl(d);
  try {
    await navigator.clipboard.writeText(url);
    toast(`Share link copied to clipboard.`);
  } catch {
    // clipboard blocked — still surface the URL so the user can copy it
    toast(`Share link: ${url}`, 8000);
    window.prompt("Copy this share link:", url);
  }
});

// ---- designer tutorial (first-run + re-openable via "?" ) ----
const TUTORIAL_FLAG = "dfodgs.tutorial.seen";
const TUTORIAL_STEPS: Array<{ title: string; body: string }> = [
  {
    title: "🗼 Don't Fall Over, Don't Get Sued",
    body: "Build real comms towers, load them with money-making gear, then crank the wind until they buckle. The two ticks in the top-left are your job: keep it Standing (Don't Fall Over) and keep every part attached (Don't Get Sued).",
  },
  {
    title: "🏗 Build a tower",
    body: "Pick a starter from the tower bar (bottom-left) — or hit Example Towers for 40+ real masts. Open ✎ Design to shape your own: type (lattice / guyed / monopole), height, bracing, materials (proper steel… or spaghetti), even the soil and footing under it.",
  },
  {
    title: "💰 Make it pay",
    body: "Hang antennas & dishes in Equipment — each earns monthly rent, shown as Monthly rent and Payback in the stats (top-right). But every dish adds weight and wind drag. The game: pay the build back as fast as you can without it toppling.",
  },
  {
    title: "🌬 Stress-test it",
    body: "Crank Wind, spin the Direction dial, and flip on Gusts (turbulent buffeting — watch Fatigue creep up over time), Ice, or Age the steel (corrosion). The top-right panel shows Peak utilization, Foundation load and Max sway as it strains.",
  },
  {
    title: "🎯 Prove it, then break it",
    body: "Open ★ Challenges (right beside the Designer) for objectives — survive a Cat-5 gust, pay back in 36 months — and hit Test. When something snaps, Replay Failure walks you through exactly which part broke, why, and at what load.",
  },
];
let tutStep = 0;
const tutEl = document.getElementById("tutorial") as HTMLDivElement;
const tutStepEl = document.getElementById("tut-step")!;
const tutDotsEl = document.getElementById("tut-dots")!;
const tutSkip = document.getElementById("tut-skip") as HTMLButtonElement;
const tutNext = document.getElementById("tut-next") as HTMLButtonElement;

function renderTutorial() {
  const s = TUTORIAL_STEPS[tutStep]!;
  tutStepEl.innerHTML = "";
  const num = document.createElement("div");
  num.className = "tut-num";
  num.textContent = `Step ${tutStep + 1} of ${TUTORIAL_STEPS.length}`;
  const h = document.createElement("h3");
  h.textContent = s.title;
  const p = document.createElement("p");
  p.textContent = s.body;
  tutStepEl.append(num, h, p);

  tutDotsEl.replaceChildren();
  for (let i = 0; i < TUTORIAL_STEPS.length; i++) {
    const dot = document.createElement("span");
    dot.className = "dot" + (i === tutStep ? " on" : "");
    tutDotsEl.appendChild(dot);
  }
  tutNext.textContent = tutStep === TUTORIAL_STEPS.length - 1 ? "Got it" : "Next";
}

function openTutorial() {
  tutStep = 0;
  renderTutorial();
  tutEl.hidden = false;
}
function closeTutorial() {
  tutEl.hidden = true;
  try {
    localStorage.setItem(TUTORIAL_FLAG, "1");
  } catch {
    /* storage disabled — fine, tutorial just shows again */
  }
}
function maybeShowTutorial() {
  let seen = false;
  try {
    seen = localStorage.getItem(TUTORIAL_FLAG) === "1";
  } catch {
    seen = false;
  }
  if (!seen) openTutorial();
}
tutNext.addEventListener("click", () => {
  if (tutStep < TUTORIAL_STEPS.length - 1) {
    tutStep++;
    renderTutorial();
  } else {
    closeTutorial();
  }
});
tutSkip.addEventListener("click", closeTutorial);
toolEls.help.addEventListener("click", openTutorial);

// --- boot ---
// Load a shared design from the URL hash if present, else the boot design.
const shared = decodeShareUrl();
if (shared?.design) {
  loadDesign(shared.design);
  toast(`Loaded shared design "${shared.design.name}".`);
} else {
  if (shared?.error) toast(`Share link problem: ${shared.error}`);
  setActiveModelFromSpec(currentDesign.spec);
  rebuild();
}
setWind(0);
maybeShowTutorial(); // first-run welcome / game onboarding

viewport.onFrame((dt) => {
  const simActive = simControlsVisible();
  // While replaying a failure, or while the Simulate pane is closed, freeze the
  // physics but keep rendering the current tower/drawing.
  if (simPaused || !simActive) {
    view?.setWind(currentWind, currentWindDir); // flag streams downwind, still when calm
    view?.update();
    appView?.update();
    updateHighlight();
    schematic.update();
    if (!simActive) {
      els.status.textContent = "Designing";
      els.status.className = "";
    }
    return;
  }

  if (ramping && !solver.collapsed) setWind(currentWind + dt * 2.5);

  simTime += dt;
  solver.step(dt);

  // feed newly-failed members to the replay recorder, each with a REAL physics
  // detail line computed from the member's own values at the instant of failure.
  if (solver.failedThisFrame.length > 0) {
    const { nodes } = built.structure;
    replay.record(
      solver.failedThisFrame.map((m) => {
        const a = nodes[m.i]!;
        const b = nodes[m.j]!;
        const buckled = m.failMode === "buckle";
        const verb =
          m.failMode === "fatigue"
            ? "fatigue-cracked"
            : m.failMode === "shear"
              ? "sheared its bolts"
              : buckled
                ? "buckled"
                : "snapped";
        return {
          key: `m${m.i}-${m.j}`, // unique per member (node pair) → full cascade, no label collisions
          position: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, z: (a.z + b.z) / 2 },
          label: `${m.section.name} ${verb}`,
          util: m.util,
          phenomenonId:
            m.failMode === "fatigue"
              ? "fatigue"
              : buckled
                ? "euler-buckling"
                : m.tensionOnly
                  ? "tension-fracture"
                  : "yielding",
          detail: failureDetail(m),
        };
      }),
      simTime,
      snapshotNodes(), // geometry at this failure moment (for rewind in replay)
    );
  }

  // Foundation / anchor failures: surface WHY the base let go (bearing / uplift /
  // sliding / anchorage) in the event log AND the failure replay — otherwise the
  // footing punching in or an anchor pulling out is invisible (looks like it just
  // topples while "Standing").
  if (solver.foundationFailedThisFrame.length > 0) {
    const pid = (mode: string) => (mode === "bearing" ? "bearing-failure" : "overturning");
    replay.record(
      solver.foundationFailedThisFrame.map((f, k) => ({
        key: `f${f.position.x.toFixed(1)},${f.position.z.toFixed(1)},${k}`,
        position: f.position,
        label: f.label,
        util: 1,
        phenomenonId: pid(f.mode),
        detail: f.detail,
      })),
      simTime,
      snapshotNodes(),
    );
    for (const f of solver.foundationFailedThisFrame) {
      eventLog.log({
        time: simTime,
        phenomenonId: pid(f.mode),
        title: `Foundation — ${f.label}`,
        detail: f.detail,
        severity: "critical",
      });
    }
  }

  // Live dynamics explainer: run the aeroelastic analyzer on the tower's actual
  // wind this frame and log every (debounced) cited event it emits.
  {
    const meanWind = currentWind;
    // Instantaneous gust: same as the mean unless the solver is running gusty,
    // in which case reproduce its per-step gustSpeed() (deterministic).
    const instWind =
      solver.params.gustiness > 0
        ? gustSpeed(
            { meanSpeed: meanWind, gustiness: solver.params.gustiness, seed: solver.params.windSeed },
            simTime,
          )
        : meanWind;
    for (const ev of analyzer.update(instWind, meanWind, dt)) {
      eventLog.log({
        time: simTime,
        phenomenonId: ev.phenomenonId,
        title: ev.title,
        detail: ev.detail,
        severity: ev.severity,
      });
    }
  }

  view?.setWind(currentWind, currentWindDir); // flag streams downwind, still when calm
  view?.update();
  appView?.update();
  updateHighlight();
  schematic.update();

  setGoal(els.gFall, !solver.collapsed); // "Don't Fall Over"
  setGoal(els.gSued, !solver.partsLost); // "Don't Get Sued"

  const pct = Math.round(solver.maxUtil * 100);
  els.util.textContent = `${pct}%`;
  els.util.className = solver.maxUtil >= 1 ? "fail" : solver.maxUtil >= 0.75 ? "warn" : "";
  // Foundation health: worst of the geotech footing + steel↔concrete connection
  // utilization (the base load path that can silently topple the tower).
  const foundU = Math.max(solver.maxFoundationUtil, solver.maxConnectionUtil);
  els.found.textContent = solver.settling ? "settling…" : `${Math.round(foundU * 100)}%`;
  els.found.className = foundU >= 1 ? "fail" : foundU >= 0.75 ? "warn" : "";
  // Fatigue: worst member's accumulated Miner damage (only accrues under gusts).
  const fat = solver.maxFatigue;
  els.fatigue.textContent = !gusty ? "— (no gusts)" : `${Math.round(fat * 100)}%`;
  els.fatigue.className = fat >= 1 ? "fail" : fat >= 0.5 ? "warn" : "";
  els.q.textContent = `${Math.round(velocityPressure(currentWind, built.height * 0.6))} Pa`;
  updateEconomicsHud(); // live rent/payback — drops to $0 if a microwave link is lost

  // Serviceability readouts: top deflection (% of height) and microwave-link
  // soft-fail status (docs/engineering/25). Read-only — never affects the solve.
  const svc = solver.serviceability;
  if (svc) {
    const driftPct = svc.topDriftFrac * 100;
    els.drift.textContent = `${driftPct.toFixed(2)}% H`;
    // TIA-222 serviceability guidance ~2–3% of height; flag past that.
    els.drift.className = driftPct >= 3 ? "fail" : driftPct >= 2 ? "warn" : "";
    // Max sway: peak horizontal top movement (max-hold) since this tower was built,
    // in metres and % of height — the "how much did it move" number.
    const swayM = svc.topDriftFrac * built.height;
    if (swayM > maxSwayM) maxSwayM = swayM;
    els.sway.textContent = `${maxSwayM.toFixed(2)} m (${((maxSwayM / built.height) * 100).toFixed(2)}% H)`;
    if (svc.dishes.length === 0) {
      els.link.textContent = "no dishes";
      els.link.className = "";
    } else if (svc.worstLink === "lost") {
      const n = svc.dishes.filter((d) => d.status === "lost").length;
      els.link.textContent = `${n} link${n > 1 ? "s" : ""} lost`;
      els.link.className = "fail";
    } else if (svc.worstLink === "degraded") {
      const n = svc.dishes.filter((d) => d.status === "degraded").length;
      els.link.textContent = `${n} degraded`;
      els.link.className = "warn";
    } else {
      els.link.textContent = `${svc.dishes.length} ok`;
      els.link.className = "ok";
    }
  }
  if (solver.collapsed) {
    els.status.textContent = "COLLAPSED";
    els.status.className = "fail";
  } else if (solver.settling) {
    els.status.textContent = "Settling…";
    els.status.className = "";
  } else if (solver.maxUtil >= 0.9) {
    els.status.textContent = "Overloaded";
    els.status.className = "warn";
  } else {
    els.status.textContent = "Standing";
    els.status.className = "ok";
  }
});

viewport.start();
